import { forwardRef, useImperativeHandle, useState, useRef, useEffect } from 'react';
import InputBoxWithUnit from '@components/ui/InputBoxWithUnit';
import CustomSelect from '@components/ui/CustomSelect';
import Spinner from '@components/ui/Spinner';
import type { FormProps } from '../../types/form.types';
import { useTheme } from '@theme/useTheme';

export interface Page0Handle {
  validate: () => boolean;
}

const Page0 = forwardRef<Page0Handle, FormProps>(({
  formData,
  updateFormData,
  onPrevious,
  onNext,
  currentPage = 0,
  canGoPrevious = false,
  canGoNext = true,
  isLoading = false,
  error,
}, ref) => {
  const { colors } = useTheme();
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Refs for each field
  const heightRef = useRef<HTMLDivElement>(null);
  const weightRef = useRef<HTMLDivElement>(null);
  const neckRef = useRef<HTMLDivElement>(null);
  const waistRef = useRef<HTMLDivElement>(null);

  // Scroll to first error when errors change
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      const firstErrorField = Object.keys(errors)[0];
      
      const fieldRefs: Record<string, React.RefObject<HTMLDivElement | null>> = {
        heightFeet: heightRef,
        heightInch: heightRef,
        weight: weightRef,
        weightUnit: weightRef,
        neckCircumference: neckRef,
        waistCircumference: waistRef,
      };

      const refToScroll = fieldRefs[firstErrorField];
      if (refToScroll?.current) {
        refToScroll.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }
  }, [errors]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Height Feet validation - Must be integer
    if (!formData.heightFeet || formData.heightFeet.trim() === '') {
      newErrors.heightFeet = 'Height (feet) is required';
    } else {
      // Check for decimal point before parsing
      if (formData.heightFeet.includes('.')) {
        newErrors.heightFeet = 'Feet must be a whole number (no decimals)';
      } else {
        const feetValue = parseInt(formData.heightFeet, 10);
        if (isNaN(feetValue)) {
          newErrors.heightFeet = 'Please enter a valid whole number';
        } else if (feetValue < 2 || feetValue > 9) {
          newErrors.heightFeet = 'Feet must be between 2 and 9';
        }
      }
    }

    // Height Inches validation - Must be integer
    if (!formData.heightInch || formData.heightInch.trim() === '') {
      newErrors.heightInch = 'Height (inches) is required';
    } else {
      // Check for decimal point before parsing
      if (formData.heightInch.includes('.')) {
        newErrors.heightInch = 'Inches must be a whole number (no decimals)';
      } else {
        const inchValue = parseInt(formData.heightInch, 10);
        if (isNaN(inchValue)) {
          newErrors.heightInch = 'Please enter a valid whole number';
        } else if (inchValue < 0 || inchValue >= 12) {
          newErrors.heightInch = 'Inches must be between 0 and 11';
        }
      }
    }

    // Validate total height is reasonable
    if (formData.heightFeet && formData.heightInch && !newErrors.heightFeet && !newErrors.heightInch) {
      const feetValue = parseInt(formData.heightFeet, 10);
      const inchValue = parseInt(formData.heightInch, 10);
      if (!isNaN(feetValue) && !isNaN(inchValue)) {
        const totalInches = (feetValue * 12) + inchValue;
        if (totalInches < 24 || totalInches > 108) {
          newErrors.heightFeet = 'Total height must be between 2\'0" and 9\'0"';
        }
      }
    }

    // Weight validation (can be decimal)
    if (!formData.weight || formData.weight.trim() === '') {
      newErrors.weight = 'Weight is required';
    } else {
      const weightValue = parseFloat(formData.weight);
      if (isNaN(weightValue)) {
        newErrors.weight = 'Please enter a valid number';
      } else if (formData.weightUnit === 'lbs') {
        if (weightValue < 50 || weightValue > 700) {
          newErrors.weight = 'Weight must be between 50 and 700 lbs';
        }
      } else if (formData.weightUnit === 'kg') {
        if (weightValue < 20 || weightValue > 320) {
          newErrors.weight = 'Weight must be between 20 and 320 kg';
        }
      }
    }

    if (!formData.weightUnit) {
      newErrors.weightUnit = 'Weight unit is required';
    }

    // Neck circumference validation (optional field, can be decimal)
    if (formData.neckCircumference && formData.neckCircumference.trim() !== '') {
      const neckValue = parseFloat(formData.neckCircumference);
      if (isNaN(neckValue)) {
        newErrors.neckCircumference = 'Please enter a valid number';
      } else if (neckValue < 10 || neckValue > 30) {
        newErrors.neckCircumference = 'Neck circumference must be between 10 and 30 inches';
      }
    }

    // Waist circumference validation (optional field, can be decimal)
    if (formData.waistCircumference && formData.waistCircumference.trim() !== '') {
      const waistValue = parseFloat(formData.waistCircumference);
      if (isNaN(waistValue)) {
        newErrors.waistCircumference = 'Please enter a valid number';
      } else if (waistValue < 20 || waistValue > 80) {
        newErrors.waistCircumference = 'Waist circumference must be between 20 and 80 inches';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useImperativeHandle(ref, () => ({
    validate,
  }));

  const clearError = (field: string) => {
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Filter input to allow only integers for height fields
  const handleHeightFeetChange = (text: string) => {
    // Remove any non-digit characters
    const cleaned = text.replace(/[^0-9]/g, '');
    updateFormData({ heightFeet: cleaned });
    clearError('heightFeet');
  };

  const handleHeightInchChange = (text: string) => {
    // Remove any non-digit characters
    const cleaned = text.replace(/[^0-9]/g, '');
    updateFormData({ heightInch: cleaned });
    clearError('heightInch');
    clearError('heightFeet');
  };

  return (
    <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <div
        className="border p-6 sm:p-8 lg:p-10"
        style={{
          backgroundColor: colors.background,
          borderColor: colors.border,
          borderTopRightRadius: '2rem',
          borderBottomLeftRadius: '2rem'
        }}
      >
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 sm:mb-6" style={{ color: colors.heading }}>
          Basic Measurements
        </h2>
        <p className="mb-6 sm:mb-8 text-base sm:text-lg" style={{ color: colors.text }}>
          Please provide your basic physical measurements. These help us assess your risk factors for sleep apnea.
        </p>

        <div className="space-y-4 sm:space-y-6">
          {/* Height Fields - Feet and Inches Side by Side */}
          <div ref={heightRef}>
            <label 
              className="block text-sm font-medium mb-2" 
              style={{ color: colors.text }}
            >
              Height <span style={{ color: colors.error }}>*</span>
            </label>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <div className="flex-1">
                <InputBoxWithUnit
                  beforeText="Feet"
                  isSecureText={false}
                  value={formData.heightFeet || ''}
                  unitValue={'ft'}
                  onChange={handleHeightFeetChange}
                  keyBoardType="numeric"
                  hasError={!!errors.heightFeet}
                  errorMessage={errors.heightFeet}
                  required
                  placeholder="e.g. 5 or 6"
                  min={2}
                  max={9}
                  description=""
                />
              </div>
              <div className="flex-1">
                <InputBoxWithUnit
                  beforeText="Inches"
                  isSecureText={false}
                  value={formData.heightInch || ''}
                  unitValue={'in'}
                  onChange={handleHeightInchChange}
                  keyBoardType="numeric"
                  hasError={!!errors.heightInch}
                  errorMessage={errors.heightInch}
                  required
                  placeholder="e.g. 0-11"
                  min={0}
                  max={11}
                  description=""
                />
              </div>
            </div>
            <p className="mt-2 text-sm" style={{ color: colors.text + 'CC' }}>
              Enter your height in whole numbers only (e.g., 5 feet 9 inches)
            </p>
          </div>

          <div ref={weightRef}>
            <InputBoxWithUnit
              beforeText="Weight"
              isSecureText={false}
              value={formData.weight || ''}
              unitValue={formData.weightUnit || 'lbs'}
              onChange={(text) => {
                updateFormData({ weight: text });
                clearError('weight');
                clearError('weightUnit');
              }}
              keyBoardType="numeric"
              hasError={!!errors.weight || !!errors.weightUnit}
              errorMessage={errors.weight || errors.weightUnit}
              required
              min={formData.weightUnit === 'lbs' ? 50 : 20}
              max={formData.weightUnit === 'lbs' ? 700 : 320}
              placeholder="Enter your weight"
            />
          </div>

          <div ref={neckRef}>
            <InputBoxWithUnit
              beforeText="Neck Circumference"
              isSecureText={false}
              value={formData.neckCircumference || ''}
              unitValue={'inches'}
              onChange={(text) => {
                updateFormData({ neckCircumference: text });
                clearError('neckCircumference');
              }}
              min={10}
              max={30}
              keyBoardType="numeric"
              hasError={!!errors.neckCircumference}
              errorMessage={errors.neckCircumference}
              placeholder="Measure around your neck"
              description="Measure around the base of your neck, just above the collarbone (optional)"
            />
          </div>

          <div ref={waistRef}>
            <InputBoxWithUnit
              beforeText="Waist Circumference"
              isSecureText={false}
              value={formData.waistCircumference || ''}
              unitValue={'inches'}
              onChange={(text) => {
                updateFormData({ waistCircumference: text });
                clearError('waistCircumference');
              }}
              min={20}
              max={80}
              keyBoardType="numeric"
              hasError={!!errors.waistCircumference}
              errorMessage={errors.waistCircumference}
              placeholder="Measure around your waist"
              description="Measure around your waist at the narrowest point (optional)"
            />
          </div>

          <CustomSelect
            value={formData.tonsilSize}
            onChange={(value) => {
              updateFormData({ tonsilSize: value === 0 ? null : value });
            }}
            options={[
              { value: 1, label: '1 - Normal' },
              { value: 2, label: '2 - Slightly Enlarged' },
              { value: 3, label: '3 - Moderately Enlarged' },
              { value: 4, label: '4 - Significantly Enlarged' },
              { value: 5, label: '5 - Severely Enlarged' },
            ]}
            placeholder="Select Tonsil Size"
            label="Tonsil Assessment"
            description="Select the size of your tonsils based on medical assessment (optional)"
          />
        </div>

        <div
          className="mt-6 sm:mt-8 p-4 sm:p-6"
          style={{
            backgroundColor: colors.secondaryButton,
            borderTopRightRadius: '1.5rem',
            borderBottomLeftRadius: '1.5rem'
          }}
        >
          <h3 className="font-semibold mb-3 text-base sm:text-lg" style={{ color: colors.heading }}>
            Why these measurements matter:
          </h3>
          <ul className="text-sm sm:text-base space-y-2" style={{ color: colors.text }}>
            <li>• <strong>BMI:</strong> Higher BMI increases sleep apnea risk</li>
            <li>• <strong>Neck size:</strong> Larger necks may have narrower airways</li>
            <li>• <strong>Waist size:</strong> Abdominal fat can affect breathing during sleep</li>
          </ul>
        </div>

        {/* Navigation Buttons */}
        {onPrevious && onNext && (
          <div
            className="mt-6 sm:mt-8 pt-6 border-t"
            style={{ borderColor: colors.border }}
          >
            {/* Error Display */}
            {error && (
              <div
                className="mb-4 p-3 rounded-lg border"
                style={{
                  backgroundColor: colors.error + '20',
                  borderColor: colors.error
                }}
              >
                <p className="text-sm" style={{ color: colors.error }}>{error}</p>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <button
                onClick={onPrevious}
                disabled={!canGoPrevious || isLoading}
                className={`w-full sm:w-auto px-6 sm:px-8 py-3 font-medium transition-all duration-200 ${canGoPrevious && !isLoading
                  ? 'hover:shadow-lg transform hover:-translate-y-0.5'
                  : 'cursor-not-allowed'
                  }`}
                style={{
                  backgroundColor: canGoPrevious && !isLoading ? colors.secondaryButton : colors.input,
                  color: canGoPrevious && !isLoading ? colors.text : colors.text + '80',
                  borderTopRightRadius: '1rem',
                  borderBottomLeftRadius: '1rem'
                }}
              >
                Previous
              </button>

              {/* Page Indicators - Centered */}
              <div className="flex items-center justify-center space-x-2">
                {[0, 1, 2, 3].map((page) => (
                  <div
                    key={page}
                    className={`w-2 h-2 rounded-full transition-all duration-200 ${page === currentPage
                      ? 'scale-110'
                      : page < currentPage
                        ? 'opacity-60'
                        : 'opacity-30'
                      }`}
                    style={{
                      backgroundColor:
                        page === currentPage
                          ? colors.primary
                          : page < currentPage
                            ? colors.primary
                            : colors.border,
                    }}
                  />
                ))}
              </div>

              <button
                onClick={onNext}
                disabled={!canGoNext || isLoading}
                className={`w-full sm:w-auto px-6 sm:px-8 py-3 font-medium text-white transition-all duration-200 ${canGoNext && !isLoading
                  ? 'hover:shadow-lg transform hover:-translate-y-0.5'
                  : 'cursor-not-allowed'
                  }`}
                style={{
                  backgroundColor: canGoNext && !isLoading ? colors.primaryButton : colors.border,
                  borderTopRightRadius: '1rem',
                  borderBottomLeftRadius: '1rem'
                }}
              >
                {isLoading ? (
                  <Spinner size="sm" color="white" text="Processing..." showText={true} />
                ) : (
                  'Next'
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

Page0.displayName = 'Page0';

export default Page0;
